import moment from 'moment';

export const GESCHLECHT_LABEL: Record<number, string> = { 0: 'männlich', 1: 'weiblich', 2: 'divers' };
const BESCHAEFTIGUNG_ART: Record<number, string> = { 0: 'Anstellung', 1: 'Ausbildung', 2: 'Arbeitslos', 3: 'Selbständig' };
const DOC_ART_LABEL: Record<number, string> = { 1: 'Reisepass', 2: 'Personalausweis' };

export function generateDiff(oldData: any, newData: any): string {
  const old = oldData;
  const lines: string[] = [];
  const now = moment().format('DD.MM.YYYY HH:mm');
  lines.push(`ZÜP Änderungsprotokoll — ${newData.famname} ${newData.vorname}`);
  lines.push(`Erstellt am ${now}`);
  lines.push('═'.repeat(60));
  lines.push('');

  let hasChanges = false;

  // Personal fields
  const fieldLabels: Record<string, string> = {
    titel: 'Titel', famname: 'Familienname', vorname: 'Vorname', gebname: 'Geburtsname',
    geschlecht: 'Geschlecht', gebdatum: 'Geburtsdatum', geb_ort: 'Geburtsort',
    geb_ort_plz: 'Geburtsort PLZ', geb_ort_land: 'Geburtsort Land',
    vorname_m: 'Vorname Mutter', vorname_v: 'Vorname Vater',
    staat_1: 'Staatsangehörigkeit 1', staat_2: 'Staatsangehörigkeit 2',
    kontakt_tel: 'Telefon', kontakt_mail: 'E-Mail',
  };

  const fieldChanges: string[] = [];
  for (const [key, label] of Object.entries(fieldLabels)) {
    let oldVal = old[key] ?? '';
    let newVal = newData[key] ?? '';
    if (key === 'geschlecht') {
      oldVal = GESCHLECHT_LABEL[old[key]] ?? oldVal;
      newVal = GESCHLECHT_LABEL[newData[key]] ?? newVal;
    }
    if (String(oldVal).toLowerCase().trim() !== String(newVal).toLowerCase().trim() && newVal !== '') {
      fieldChanges.push(`  ${label}: "${oldVal}" → "${newVal}"`);
    }
  }
  if (fieldChanges.length > 0) {
    hasChanges = true;
    lines.push('PERSÖNLICHE DATEN');
    lines.push('-'.repeat(40));
    lines.push(...fieldChanges);
    lines.push('');
  }

  // Addresses
  const oldAddr = old.adresse || [];
  const newAddr = newData.adresse || [];
  const addrNew: any[] = [];
  const addrUpdated: { item: any; changes: string[] }[] = [];
  const addrRemoved: any[] = [];

  const usedOldAddrIndices = new Set<number>();
  for (const na of newAddr) {
    const exactIdx = oldAddr.findIndex((oa: any, idx: number) =>
      !usedOldAddrIndices.has(idx) &&
      oa.strasse?.toLowerCase() === na.strasse?.toLowerCase() &&
      oa.zusatz?.toLowerCase() === na.zusatz?.toLowerCase() &&
      oa.ort?.toLowerCase() === na.ort?.toLowerCase() &&
      oa.von === na.von
    );
    const fallbackIdx = exactIdx >= 0 ? exactIdx : oldAddr.findIndex((oa: any, idx: number) =>
      !usedOldAddrIndices.has(idx) &&
      oa.strasse?.toLowerCase() === na.strasse?.toLowerCase() &&
      oa.zusatz?.toLowerCase() === na.zusatz?.toLowerCase() &&
      oa.ort?.toLowerCase() === na.ort?.toLowerCase()
    );
    const finalIdx = exactIdx >= 0 ? exactIdx : fallbackIdx;
    if (finalIdx < 0) {
      addrNew.push(na);
    } else {
      usedOldAddrIndices.add(finalIdx);
      const matched = oldAddr[finalIdx];
      const changes: string[] = [];
      if (matched.bis !== na.bis) changes.push(`  Bis: "${matched.bis || 'aktuell'}" → "${na.bis || 'aktuell'}"`);
      if (matched.von !== na.von) changes.push(`  Von: "${matched.von}" → "${na.von}"`);
      if (matched.plz !== na.plz) changes.push(`  PLZ: "${matched.plz}" → "${na.plz}"`);
      if (changes.length > 0) addrUpdated.push({ item: na, changes });
    }
  }
  for (let i = 0; i < oldAddr.length; i++) {
    if (!usedOldAddrIndices.has(i)) addrRemoved.push(oldAddr[i]);
  }

  if (addrNew.length > 0 || addrUpdated.length > 0 || addrRemoved.length > 0) {
    hasChanges = true;
    lines.push('WOHNSITZE');
    lines.push('-'.repeat(40));
    for (const a of addrNew) {
      lines.push(`  + NEU: ${a.strasse} ${a.zusatz}, ${a.plz} ${a.ort} (${a.land})  ${a.von} – ${a.bis || 'aktuell'}`);
    }
    for (const u of addrUpdated) {
      lines.push(`  ~ GEÄNDERT: ${u.item.strasse} ${u.item.zusatz}, ${u.item.ort}`);
      lines.push(...u.changes);
    }
    for (const a of addrRemoved) {
      lines.push(`  - ENTFERNT: ${a.strasse} ${a.zusatz}, ${a.plz} ${a.ort} (${a.land})  ${a.von} – ${a.bis || 'aktuell'}`);
    }
    lines.push('');
  }

  // Employment
  const oldWork = old.beschaeftigung || [];
  const newWork = newData.beschaeftigung || [];
  const workNew: any[] = [];
  const workUpdated: { item: any; changes: string[] }[] = [];
  const workRemoved: any[] = [];

  const usedOldWorkIndices = new Set<number>();
  for (const nw of newWork) {
    const matchIdx = oldWork.findIndex((ow: any, idx: number) =>
      !usedOldWorkIndices.has(idx) &&
      ow.name?.toLowerCase() === nw.name?.toLowerCase() &&
      ow.von === nw.von
    );
    const fallbackIdx = matchIdx >= 0 ? matchIdx : oldWork.findIndex((ow: any, idx: number) =>
      !usedOldWorkIndices.has(idx) &&
      ow.name?.toLowerCase() === nw.name?.toLowerCase() &&
      ow.art === nw.art
    );
    const finalIdx = matchIdx >= 0 ? matchIdx : fallbackIdx;
    if (finalIdx < 0) {
      workNew.push(nw);
    } else {
      usedOldWorkIndices.add(finalIdx);
      const matched = oldWork[finalIdx];
      const changes: string[] = [];
      if (matched.bis !== nw.bis) changes.push(`  Bis: "${matched.bis || 'aktuell'}" → "${nw.bis || 'aktuell'}"`);
      if (matched.info !== nw.info) changes.push(`  Tätigkeit: "${matched.info || ''}" → "${nw.info || ''}"`);
      if (matched.von !== nw.von) changes.push(`  Von: "${matched.von}" → "${nw.von}"`);
      if (changes.length > 0) workUpdated.push({ item: nw, changes });
    }
  }
  for (let i = 0; i < oldWork.length; i++) {
    if (!usedOldWorkIndices.has(i)) workRemoved.push(oldWork[i]);
  }

  if (workNew.length > 0 || workUpdated.length > 0 || workRemoved.length > 0) {
    hasChanges = true;
    lines.push('BESCHÄFTIGUNGEN');
    lines.push('-'.repeat(40));
    for (const w of workNew) {
      const artLabel = BESCHAEFTIGUNG_ART[parseInt(w.art)] || w.art;
      lines.push(`  + NEU: ${w.name} (${artLabel}: ${w.info || ''})  ${w.von} – ${w.bis || 'aktuell'}`);
    }
    for (const u of workUpdated) {
      lines.push(`  ~ GEÄNDERT: ${u.item.name}`);
      lines.push(...u.changes);
    }
    for (const w of workRemoved) {
      lines.push(`  - ENTFERNT: ${w.name} (${w.info || ''})  ${w.von} – ${w.bis || 'aktuell'}`);
    }
    lines.push('');
  }

  // Documents
  const oldDocs = old.dokument || [];
  const newDocs = newData.dokument || [];
  const docChanges: string[] = [];

  const usedOldDocIndices = new Set<number>();
  for (const nd of newDocs) {
    const exactIdx = oldDocs.findIndex((od: any, idx: number) =>
      !usedOldDocIndices.has(idx) && od.nummer === nd.nummer && od.art === nd.art && od.name === nd.name
    );
    const fallbackIdx = exactIdx >= 0 ? exactIdx : oldDocs.findIndex((od: any, idx: number) =>
      !usedOldDocIndices.has(idx) && od.nummer === nd.nummer && od.art === nd.art
    );
    const finalIdx = exactIdx >= 0 ? exactIdx : fallbackIdx;
    if (finalIdx < 0) {
      const artLabel = DOC_ART_LABEL[nd.art] || nd.art;
      docChanges.push(`  + NEU: ${artLabel} Nr. ${nd.nummer}, ausgestellt ${nd.ausstellung}, Datei: ${nd.name}`);
    } else {
      usedOldDocIndices.add(finalIdx);
      const matched = oldDocs[finalIdx];
      if (matched.name !== nd.name || matched.ausstellung !== nd.ausstellung) {
        const artLabel = DOC_ART_LABEL[nd.art] || nd.art;
        if (matched.ausstellung !== nd.ausstellung) {
          docChanges.push(`  ~ ${artLabel} Nr. ${nd.nummer}: Ausstellung "${matched.ausstellung}" → "${nd.ausstellung}"`);
        }
        if (matched.name !== nd.name) {
          docChanges.push(`  ~ ${artLabel} Nr. ${nd.nummer}: Datei "${matched.name}" → "${nd.name}"`);
        }
      }
    }
  }

  if (docChanges.length > 0) {
    hasChanges = true;
    lines.push('DOKUMENTE');
    lines.push('-'.repeat(40));
    lines.push(...docChanges);
    lines.push('');
  }

  if (!hasChanges) {
    lines.push('Keine Änderungen zum Vorjahr festgestellt.');
    lines.push('');
  }

  lines.push('═'.repeat(60));
  return lines.join('\n');
}
